<script setup>
import { 
	ref, 
	computed,
	inject,
	watch,
	onMounted,
} from 'vue'
import SignatureInput from '@/components/inputs/SignatureInput.vue'
import { useInspectionStore } from '@/stores/inspection-store'
import DamagePosInput from '@/components/inputs/DamagePosInput.vue'
import { vehicleImageUrl } from '@/modules/util/vehicle-image'
import { damageNumbers } from '@/modules/util/damage-number'
import { mdiCheck, mdiClose } from '@mdi/js'

const props = defineProps(['modelValue', 'field'])
const emit = defineEmits(['update:modelValue', 'signed'])
const store = useInspectionStore()
const saveInspection = inject('saveInspection', null)

const show = ref(false)
const pad = ref(null)
const email = ref('')
const emailRule = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) || 'Enter a valid email address'
const n = ref(0)
const items = computed(() => {
	const vals = store.currentDamageItems
	return vals ? vals : []
})
const numbers = computed(() => damageNumbers(items.value))

const clear = () => {
	pad.value.pad.clearSignature()
	pad.value.pad.openSignaturePad()
	reset()
}

const setSignedAt = (signedAt) => {
	if (store.currentInspection) store.currentInspection.signature_datetime = signedAt
}

const setEmail = (signatureEmail) => {
	if (store.currentInspection) store.currentInspection.signature_email = signatureEmail
}

const reset = () => {
	emit('update:modelValue', null)
	setSignedAt(null)
	setEmail(null)
}

// Done signs and saves the inspection; closing the dialog any other way discards the signature
let signed = false

const done = async () => {
	if (emailRule(email.value) !== true) {
		return
	}

	const { isEmpty, data } = pad.value.pad.saveSignature()

	if (isEmpty) {
		return
	}

	emit('update:modelValue', data)
	setSignedAt(new Date().toISOString())
	setEmail(email.value.trim() || null)
	signed = true
	show.value = false

	if (saveInspection && !(await saveInspection())) {
		reset()
	}
}

watch(show, (open) => {
	if (!open && !signed) {
		reset()
	}
	if (!open) {
		email.value = ''
	}
	signed = false
})

onMounted(() => {
	reset()
})
</script>

<template>
<div class="d-flex w-100">
	<v-btn
		class="ma-auto mt-5"
		size="large"
		color="primary"
		variant="tonal"
		@click="show = true"
	>
		Request Customer Signature
	</v-btn>

	<v-dialog
		v-model="show"
		fullscreen
	>
		<v-card>
			<v-card-title>
				<v-btn
					:icon="mdiClose"
					size="small"
					class="float-right"
					@click="show = false"
				/>

				{{ field.label }}
				<v-card-subtitle class="ps-0">
					{{ field.description }}
				</v-card-subtitle>
			</v-card-title>
			
			<v-card-text class="px-4 pb-0">
				<v-window
					v-model="n"
					class="mx-auto w-100 h-100"
					show-arrows
				>
					<v-window-item
						v-for="(item, i) in items"
						:key="i"
						:value="i"
						class="h-100"
					>
						<v-card
							class="h-100 pb-0"
							elevation="0"
							style="gap: 2rem; max-height: calc(100vh - 86px); overflow: hidden; overflow-y: auto;"
						>
							<div
								class="d-flex flex-column align-center mx-auto"
								style="width: clamp(300px, 100%, 500px);"
							>
								<DamagePosInput
									v-if="item.damagemappos"
									:model-value="item.damagemappos"
									:splat="store.currentInspection.splat"
									:number="numbers.get(item)"
									:readonly="true"
								/>
								<v-list>
									<v-list-item class="pa-0">
										<v-list-item-subtitle>
											{{ item.date }}
										</v-list-item-subtitle>

										<!-- NAMA damage item -->
										<v-list-item-title v-if="item.category">
											{{ item.category }} - {{ item.component }} - {{ item.condition }} - {{ item.severity }}
										</v-list-item-title>

										<!-- Motorbike  damage item-->
										<v-list-item-title v-else-if="item.component">
											{{ item.component }} - {{ item.repair?.repair }}
										</v-list-item-title>
									</v-list-item>
								</v-list>
								<img
									v-if="item.imagename || item.image"
									:src="item.image || vehicleImageUrl(item.imagename)"
									style="border-radius: 0.25rem; object-fit: cover; width: 100%;"
								>
							</div>
						</v-card>
					</v-window-item>
					<v-window-item
						:value="items.length"
						class="h-100"
					>
						<div class="h-100 d-flex">
							<v-card class="ma-auto">
								<v-card-text>
									<v-text-field
										v-model="email"
										label="Email a copy (optional)"
										type="email"
										variant="underlined"
										color="primary"
										:rules="[emailRule]"
									/>
									<v-card variant="tonal">
										<SignatureInput
											ref="pad"
											:height="field.height"
											:width="field.width"
										/>
									</v-card>
								</v-card-text>
								<v-card-actions class="pb-0">
									<v-btn @click="clear">
										Clear
									</v-btn>
									<v-btn
										:color="props.modelValue ? 'success' : 'primary'"
										:append-icon="props.modelValue ? mdiCheck : ''"
										@click="done"
									>
										Done
									</v-btn>
								</v-card-actions>
							</v-card>
						</div>
					</v-window-item>
				</v-window>
			</v-card-text>
		</v-card>
	</v-dialog>
</div>
</template>

<style lang="scss" scoped>

</style>